import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Building2,
  CalendarCheck,
  Check,
  CheckCircle2,
  CircleHelp,
  Clock3,
  DatabaseZap,
  Edit3,
  Images,
  Laptop2,
  LoaderCircle,
  Mail,
  MapPin,
  Plus,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Star,
  Target,
  Trash2,
  UsersRound,
  X,
} from "lucide-react";
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  bookOnboardingMeeting,
  completeOnboarding,
  inviteTokenFromPath,
  loadOnboarding,
  loadSchedulingAvailability,
  resetPreview,
  saveDraft,
  submitFormFeedback,
} from "./api";
import { completedOnboardingState } from "./access-state";
import {
  chooseResumableDraft,
  DraftSyncCoordinator,
  localDraftStorageKey,
  readLocalDraft,
} from "./draft-store";
import {
  getFlow,
  getChapterProgress,
  getStepErrors,
  parseZipCodes,
  resolveResumableStep,
  selectedLocations,
  STEP_PREFIX,
  targetZipCodes,
} from "@shared/flow";
import type {
  BookingConfirmation,
  ClientContext,
  FlowStep,
  LocationAnswers,
  OnboardingAnswers,
  OnboardingBootstrap,
  SchedulingAvailability,
} from "@shared/types";

type SaveState = "idle" | "saving" | "saved" | "error" | "conflict";

const LOGO_URL = "/brand/hvac-launch-logo.png";
const HELP_FALLBACK_EMAIL = "team@hvaclaunch.ai";
const HELP_CC_EMAILS = ["yoojin@hvaclaunch.ai", "aaron@hvaclaunch.ai"];

function helpEmailHref(cpEmail?: string) {
  const recipient = cpEmail?.trim() || HELP_FALLBACK_EMAIL;
  const params = new URLSearchParams({
    cc: HELP_CC_EMAILS.join(","),
    subject: "Help with my HVAC Launch kickoff",
  });
  return `mailto:${recipient}?${params.toString()}`;
}

function App() {
  const [bootstrap, setBootstrap] = useState<OnboardingBootstrap | null>(null);
  const [answers, setAnswers] = useState<OnboardingAnswers | null>(null);
  const [currentStepId, setCurrentStepId] = useState("welcome");
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [stepErrors, setStepErrors] = useState<string[]>([]);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [booking, setBooking] = useState<BookingConfirmation | null>(null);
  const [reviewEditStepId, setReviewEditStepId] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const hasMountedAnswers = useRef(false);
  const draftCoordinator = useRef<DraftSyncCoordinator | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadOnboarding()
      .then((data) => {
        if (cancelled) return;
        let loadedAnswers = data.answers;
        let loadedStep =
          data.currentStep || (data.answers.submitted ? "scheduler" : "welcome");
        let recoveryWarning = data.recoveryWarning;
        const token = inviteTokenFromPath();
        if (data.mode === "live" && token) {
          const storageKey = localDraftStorageKey(token);
          const resumable = chooseResumableDraft({
            server: {
              answers: data.answers,
              currentStep: loadedStep,
              revision: data.revision,
              updatedAt: data.submissionUpdatedAt,
              lastMutationId: data.lastMutationId,
            },
            local: readLocalDraft(window.localStorage, storageKey),
          });
          loadedAnswers = resumable.answers;
          loadedStep = resumable.currentStep;
          loadedStep = resolveResumableStep(
            data.client,
            loadedAnswers,
            loadedStep,
          );
          if (resumable.source === "local") {
            recoveryWarning = recoveryWarning ??
              "We recovered unsynced answers from this browser and are saving them now.";
          } else if (resumable.preservedLocalDraft) {
            recoveryWarning = recoveryWarning ??
              "A newer server copy was restored. The older browser draft remains preserved on this device.";
          }
          const coordinator = new DraftSyncCoordinator({
            initialRevision: data.revision,
            initialMutationId: data.lastMutationId,
            storage: window.localStorage,
            storageKey,
            transport: (request, options) => saveDraft(request, options),
            onStateChange: (state) => {
              if (!cancelled) {
                setSaveState(state === "conflict" ? "conflict" : state);
              }
            },
          });
          draftCoordinator.current = coordinator;
          if (resumable.source === "local") {
            coordinator.stage({ answers: loadedAnswers, currentStep: loadedStep });
          }
        }
        loadedStep = resolveResumableStep(data.client, loadedAnswers, loadedStep);
        setBootstrap({ ...data, recoveryWarning });
        setAnswers(loadedAnswers);
        setBooking(data.booking ?? null);
        setCurrentStepId(loadedStep);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setLoadingError(
          error instanceof Error
            ? error.message
            : "This kickoff form could not be opened.",
        );
      });
    return () => {
      cancelled = true;
      draftCoordinator.current?.dispose();
    };
  }, []);

  const completedState = bootstrap
    ? completedOnboardingState({
        mode: bootstrap.mode,
        status: bootstrap.status,
        meetingBooked: Boolean(bootstrap.booking) || bootstrap.answers.meetingBooked,
      })
    : null;
  const flow = useMemo(
    () =>
      bootstrap && answers && !completedState
        ? getFlow(bootstrap.client, answers)
        : [],
    [answers, bootstrap, completedState],
  );
  const currentIndex = Math.max(
    0,
    flow.findIndex((step) => step.id === currentStepId),
  );
  const currentStep = flow[currentIndex];
  const isReviewEditMode =
    reviewEditStepId !== null && currentStep?.id !== "review";

  useEffect(() => {
    if (!bootstrap || !answers || !currentStep) return;
    if (!hasMountedAnswers.current) {
      hasMountedAnswers.current = true;
      return;
    }
    if (answers.submitted) return;
    if (bootstrap.mode === "live" && draftCoordinator.current) {
      draftCoordinator.current.stage({ answers, currentStep: currentStep.id });
      return;
    }

    setSaveState("saving");
    const timer = window.setTimeout(() => {
      void saveDraft({
        answers,
        currentStep: currentStep.id,
        expectedRevision: 0,
        mutationId: crypto.randomUUID(),
      })
        .then(() => setSaveState("saved"))
        .catch(() => setSaveState("error"));
    }, 650);
    return () => window.clearTimeout(timer);
  }, [answers, bootstrap, currentStep]);

  useEffect(() => {
    if (bootstrap?.mode !== "live") return;
    const flushBeforeLeaving = () => {
      void draftCoordinator.current?.flush(true);
    };
    const flushWhenHidden = () => {
      if (document.visibilityState === "hidden") flushBeforeLeaving();
    };
    window.addEventListener("pagehide", flushBeforeLeaving);
    document.addEventListener("visibilitychange", flushWhenHidden);
    return () => {
      window.removeEventListener("pagehide", flushBeforeLeaving);
      document.removeEventListener("visibilitychange", flushWhenHidden);
    };
  }, [bootstrap?.mode]);

  useEffect(() => {
    setStepErrors([]);
    window.scrollTo({ top: 0, behavior: "auto" });
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "auto" });
      headingRef.current?.focus({ preventScroll: true });
    });
  }, [currentStepId]);

  useEffect(() => {
    setStepErrors([]);
  }, [answers]);

  const goToStep = (stepId: string) => {
    const targetIndex = flow.findIndex((step) => step.id === stepId);
    setDirection(targetIndex < currentIndex ? "back" : "forward");
    setCurrentStepId(stepId);
  };

  const editFromReview = (stepId: string) => {
    setReviewEditStepId(stepId);
    goToStep(stepId);
  };

  const goBack = () => {
    if (currentIndex <= 0) return;
    setDirection("back");
    setCurrentStepId(flow[currentIndex - 1].id);
  };

  const returnToReview = () => {
    setStepErrors([]);
    setReviewEditStepId(null);
    setDirection("forward");
    setCurrentStepId("review");
  };

  const goNext = async () => {
    if (!bootstrap || !answers || !currentStep) return;
    const errors = getStepErrors(currentStep.id, bootstrap.client, answers);
    if (errors.length > 0) {
      setStepErrors(errors);
      return;
    }

    if (currentStep.id === "review") {
      setIsSubmitting(true);
      setStepErrors([]);
      const completedAnswers = { ...answers, submitted: true };
      try {
        await draftCoordinator.current?.flush();
        const result = await completeOnboarding(completedAnswers, {
          expectedRevision:
            draftCoordinator.current?.revision ?? bootstrap.revision,
          mutationId: crypto.randomUUID(),
        });
        draftCoordinator.current?.acknowledgeCompletion(
          { answers: completedAnswers, currentStep: "scheduler" },
          result,
        );
        setAnswers(completedAnswers);
        setDirection("forward");
        setCurrentStepId("scheduler");
      } catch (error) {
        setStepErrors([
          error instanceof Error
            ? error.message
            : "We could not submit your answers. Please try again.",
        ]);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setStepErrors([]);
    const nextFlow = getFlow(bootstrap.client, answers);
    const index = nextFlow.findIndex((step) => step.id === currentStep.id);
    const next = nextFlow[index + 1];
    if (!next) return;
    setDirection("forward");
    setCurrentStepId(next.id);
  };

  const confirmMeetingBooked = (confirmedBooking: BookingConfirmation) => {
    if (!answers) return;
    const updated = { ...answers, meetingBooked: true };
    setBooking(confirmedBooking);
    setAnswers(updated);
    setDirection("forward");
    setCurrentStepId("complete");
  };

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key !== "Enter" || event.shiftKey || event.metaKey || event.ctrlKey) return;
      const target = event.target as HTMLElement | null;
      if (
        target?.tagName === "TEXTAREA" ||
        target?.tagName === "BUTTON" ||
        target?.getAttribute("role") === "button"
      ) {
        return;
      }
      event.preventDefault();
      void goNext();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  });

  if (loadingError) {
    return (
      <FullPageState
        icon={<AlertCircle />}
        eyebrow="Link unavailable"
        title="We couldn’t open this kickoff form."
        copy={loadingError}
        action={
          <a className="button button-primary" href="mailto:team@hvaclaunch.ai">
            Contact HVAC Launch <Mail size={17} />
          </a>
        }
      />
    );
  }

  if (!bootstrap || !answers) {
    return (
      <FullPageState
        icon={<LoaderCircle className="spin" />}
        eyebrow="Client kickoff"
        title="Getting your launch plan ready…"
        copy="We’re securely loading the information your team already shared."
      />
    );
  }

  if (completedState) {
    return (
      <>
        <FullPageState
          variant="success"
          icon={<CheckCircle2 />}
          eyebrow={completedState.eyebrow}
          title={completedState.title}
          copy={completedState.copy}
        />
        {booking && (
          <ReviewCaptureWidget
            client={bootstrap.client}
            previewMode={false}
          />
        )}
      </>
    );
  }

  if (!currentStep) {
    return (
      <FullPageState
        icon={<LoaderCircle className="spin" />}
        eyebrow="Client kickoff"
        title="Getting your launch plan ready…"
        copy="We’re securely loading the information your team already shared."
      />
    );
  }

  const isTerminal = currentStep.id === "complete";
  const isScheduler = currentStep.id === "scheduler";
  const chapterProgress = getChapterProgress(flow, currentStep.id);

  return (
    <main className="app-shell">
      <Atmosphere />
      <header className="topbar">
        <img src={LOGO_URL} className="brand-logo" alt="HVAC Launch" />
        <div className="topbar-meta">
          {bootstrap.mode === "preview" && (
            <button className="preview-pill" type="button" onClick={resetPreview}>
              <Sparkles size={14} />
              <span>Interactive preview</span>
              <RotateCcw size={13} aria-hidden="true" />
            </button>
          )}
          <SaveIndicator state={saveState} mode={bootstrap.mode} />
        </div>
      </header>

      {currentStep.id !== "welcome" && (
        <div
          className="progress-shell"
          aria-label={`Section ${chapterProgress.currentChapter.number} of 6: ${chapterProgress.currentChapter.label}`}
        >
          <div className="progress-segments" aria-hidden="true">
            {chapterProgress.segments.map((chapter) => (
              <span
                key={chapter.id}
                className={`chapter-segment is-${chapter.state}`}
                style={{ "--chapter-fill": `${chapter.fill}%` } as React.CSSProperties}
              />
            ))}
          </div>
          <div className="progress-labels" aria-hidden="true">
            {chapterProgress.segments.map((chapter) => (
              <span
                key={chapter.id}
                className={chapter.id === chapterProgress.currentChapter.id ? "is-active" : ""}
              >
                {chapter.label}
              </span>
            ))}
          </div>
        </div>
      )}

      <section className="flow-stage" aria-live="polite">
        <div
          className="step-panel"
          key={currentStep.id}
          data-direction={direction}
          data-section={`onboarding.${currentStep.section.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <StepContent
            step={currentStep}
            client={bootstrap.client}
            answers={answers}
            setAnswers={setAnswers}
            headingRef={headingRef}
            onJump={editFromReview}
            previewMode={bootstrap.mode === "preview"}
            onMeetingBooked={confirmMeetingBooked}
            booking={booking}
          />

          {!isTerminal && !isScheduler && (
            <div
              className={`step-actions ${isReviewEditMode ? "is-review-edit" : ""}`}
            >
              <div>
                {currentIndex > 0 && (
                  <button
                    className="button button-quiet"
                    type="button"
                    onClick={goBack}
                  >
                    <ArrowLeft size={18} />
                    Back
                  </button>
                )}
              </div>
              {isReviewEditMode && (
                <button
                  className="button button-review-return"
                  type="button"
                  onClick={returnToReview}
                  data-cta="kickoff.review.return"
                >
                  <ArrowLeft size={18} /> Back to review
                </button>
              )}
              <button
                className="button button-primary"
                type="button"
                onClick={() => void goNext()}
                disabled={isSubmitting}
                data-cta={
                  currentStep.id === "review"
                    ? "onboarding.submit"
                    : "onboarding.next"
                }
              >
                {isSubmitting ? (
                  <>
                    <LoaderCircle className="spin" size={18} />
                    Sending securely
                  </>
                ) : currentStep.id === "welcome" ? (
                  <>
                    Let’s get started <ArrowRight size={18} />
                  </>
                ) : currentStep.id === "review" ? (
                  <>
                    Submit & book a time <CalendarCheck size={18} />
                  </>
                ) : currentStep.id.startsWith(STEP_PREFIX.reviewLocation) ? (
                  <>
                    Approve location & continue <ArrowRight size={18} />
                  </>
                ) : (
                  <>
                    Continue <ArrowRight size={18} />
                  </>
                )}
                {!isSubmitting && <kbd>Enter ↵</kbd>}
              </button>
            </div>
          )}

          {stepErrors.length > 0 && (
            <div className="error-panel" role="alert">
              <AlertCircle size={19} />
              <div>
                {stepErrors.map((error) => (
                  <p key={error}>{error}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <footer className="page-footer">
        <span>Secure client kickoff</span>
        <span aria-hidden="true">•</span>
        <a href={helpEmailHref(bootstrap.client.cpEmail)}>Need help?</a>
      </footer>
      {isTerminal && booking && (
        <ReviewCaptureWidget
          client={bootstrap.client}
          previewMode={bootstrap.mode === "preview"}
        />
      )}
    </main>
  );
}

function ReviewCaptureWidget({
  client,
  previewMode,
}: {
  client: ClientContext;
  previewMode: boolean;
}) {
  const storageKey = `hvac-launch:form-feedback:${client.clientId}`;
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState("");
  const [isDismissed, setIsDismissed] = useState(() =>
    previewMode ? false : window.localStorage.getItem(storageKey) !== null,
  );

  const rememberAndDismiss = () => {
    if (!previewMode) window.localStorage.setItem(storageKey, "sent");
    setIsDismissed(true);
  };

  const dismissWithoutFeedback = () => {
    if (!previewMode) window.localStorage.setItem(storageKey, "dismissed");
    setIsDismissed(true);
  };

  const sendFeedback = async () => {
    if (!rating || isSending) return;
    setIsSending(true);
    setError("");
    try {
      await submitFormFeedback({ rating, feedback });
      if (!previewMode) window.localStorage.setItem(storageKey, "sent");
      setIsSent(true);
    } catch (submissionError) {
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : "Your feedback could not be sent. Please try again.",
      );
    } finally {
      setIsSending(false);
    }
  };

  if (isDismissed) return null;

  return (
    <aside
      className={`review-capture ${rating ? "has-rating" : ""} ${isSent ? "is-sent" : ""}`}
      role="dialog"
      aria-label="Onboarding form feedback"
      aria-modal="false"
    >
      {isSent ? (
        <div className="review-capture-success" role="status">
          <span><Check size={17} strokeWidth={3} /></span>
          <div>
            <strong>Thank you!</strong>
            <p>Your feedback helps us improve this form.</p>
          </div>
          <button
            type="button"
            aria-label="Close feedback"
            onClick={rememberAndDismiss}
          >
            <X size={17} />
          </button>
        </div>
      ) : (
        <>
          <div className="review-capture-heading">
            <div>
              <span>Quick feedback</span>
              <strong>How easy was this form to complete?</strong>
            </div>
            <button
              type="button"
              aria-label="Close feedback"
              onClick={dismissWithoutFeedback}
            >
              <X size={17} />
            </button>
          </div>
          <div
            className="star-rating"
            role="group"
            aria-label="Rate form ease from 1 to 5 stars"
            onMouseLeave={() => setHoveredRating(0)}
          >
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                className={value <= (hoveredRating || rating) ? "is-active" : ""}
                aria-label={`${value} star${value === 1 ? "" : "s"}`}
                aria-pressed={rating === value}
                onMouseEnter={() => setHoveredRating(value)}
                onFocus={() => setHoveredRating(value)}
                onBlur={() => setHoveredRating(0)}
                onClick={() => {
                  setRating(value);
                  setError("");
                }}
              >
                <Star size={27} />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <div className="review-capture-details">
              <p className="review-thanks"><CheckCircle2 size={15} />Thanks for letting us know.</p>
              <label>
                <span>What, if anything, could have made this form easier to complete?</span>
                <textarea
                  value={feedback}
                  maxLength={2_000}
                  rows={3}
                  placeholder="Optional feedback"
                  onChange={(event) => setFeedback(event.target.value)}
                />
              </label>
              {error && <p className="review-capture-error" role="alert">{error}</p>}
            </div>
          )}
          {rating > 0 && (
            <div className="review-capture-actions">
              <button
                className="review-send"
                type="button"
                disabled={isSending}
                onClick={() => void sendFeedback()}
              >
                {isSending ? <LoaderCircle className="spin" size={15} /> : null}
                {isSending ? "Sending…" : "Send feedback"}
              </button>
            </div>
          )}
        </>
      )}
    </aside>
  );
}

function StepContent({
  step,
  client,
  answers,
  setAnswers,
  headingRef,
  onJump,
  previewMode,
  onMeetingBooked,
  booking,
}: {
  step: FlowStep;
  client: ClientContext;
  answers: OnboardingAnswers;
  setAnswers: Dispatch<SetStateAction<OnboardingAnswers | null>>;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  onJump: (stepId: string) => void;
  previewMode: boolean;
  onMeetingBooked: (booking: BookingConfirmation) => void;
  booking: BookingConfirmation | null;
}) {
  const update = (patch: Partial<OnboardingAnswers>) =>
    setAnswers((current) => (current ? { ...current, ...patch } : current));

  const updateLocation = (
    id: string,
    patch: Partial<LocationAnswers>,
  ) =>
    setAnswers((current) =>
      current
        ? {
            ...current,
            locationDetails: {
              ...current.locationDetails,
              [id]: { ...current.locationDetails[id], ...patch },
            },
          }
        : current,
    );

  if (step.id === "welcome") {
    const assignedCpName = client.cpName?.trim();
    return (
      <QuestionFrame
        eyebrow="Your launch starts here"
        title={
          <>
            {client.brandName} team, let’s get you{" "}
            <span className="red-text">launch ready.</span>
          </>
        }
        headingRef={headingRef}
        wide
        tone="welcome"
      >
        <p className="lead-copy">
          This 10–15 minute form is <strong>CRITICAL</strong> to making sure we can
          launch your campaign on time. After you finish, you’ll be able to book
          a kickoff call with our team.
        </p>
        <div className="promise-grid">
          <PromiseCard
            icon={<DatabaseZap />}
            label="Critical information"
            copy="We CAN’T EVEN START to build your campaign until you complete this form."
          />
          <PromiseCard
            icon={<Clock3 />}
            label="Save as you go"
            copy="Close the tab anytime. You and your team can return from the same link."
          />
          <PromiseCard
            icon={<Target />}
            label="Already pre-filled"
            copy="We’ve prefilled the information that we could verify."
          />
        </div>
        <div className="personal-note">
          <span
            className="avatar"
            aria-label={
              assignedCpName
                ? `${assignedCpName}, assigned client partner`
                : "Assigned HVAC Launch client partner"
            }
          >
            {initialsForName(assignedCpName)}
          </span>
          <div>
            <span className="personal-note-role">
              Your assigned client partner
            </span>
            <strong>
              {assignedCpName || "Your HVAC Launch team"} is standing by.
            </strong>
            <span>Questions? Reply to the email that brought you here.</span>
          </div>
        </div>
      </QuestionFrame>
    );
  }

  if (step.id === "locations") {
    return (
      <QuestionFrame
        eyebrow="Business footprint"
        title="We found the following business locations on your website:"
        description="Select all locations that need more sales opportunities."
        headingRef={headingRef}
      >
        <div className="selection-list">
          {client.locations.map((location, index) => {
            const selected = answers.selectedLocationIds.includes(location.id);
            return (
              <button
                key={location.id}
                className={`location-card ${selected ? "is-selected" : ""}`}
                type="button"
                onClick={() =>
                  update({
                    selectedLocationIds: selected
                      ? answers.selectedLocationIds.filter((id) => id !== location.id)
                      : [...answers.selectedLocationIds, location.id],
                  })
                }
              >
                <span className="choice-key">{String.fromCharCode(65 + index)}</span>
                <span className="location-icon"><Building2 /></span>
                <span className="location-copy">
                  <strong>{location.name}</strong>
                  <small>{location.address}</small>
                </span>
                <span className="check-control">
                  {selected && <Check size={17} strokeWidth={3} />}
                </span>
              </button>
            );
          })}
        </div>
        <p className="field-hint location-selection-hint">
          <CheckCircle2 size={15} /> Require at least one selection.
        </p>
      </QuestionFrame>
    );
  }

  if (step.id.startsWith(STEP_PREFIX.reviewLocation)) {
    const id = step.id.slice(STEP_PREFIX.reviewLocation.length);
    const location = client.locations.find((item) => item.id === id)!;
    const detail = answers.locationDetails[id];
    const reviewLocations = selectedLocations(client, answers);
    const isSingleLocationPath = reviewLocations.length === 1;
    return (
      <QuestionFrame
        eyebrow={isSingleLocationPath ? "Business information" : `${location.name} • Business information`}
        title="Please confirm or edit what we’ve found from your website:"
        headingRef={headingRef}
      >
        <div className="review-stack">
          <EditableReviewCard
            label="License number"
            value={detail.licenseNumber}
            placeholder="Enter license number"
            onChange={(value) => updateLocation(id, { licenseNumber: value })}
          />
          <EditableCallCoverageCard
            value={detail}
            onChange={(patch) => updateLocation(id, patch)}
          />
          <EditableReviewCard
            label="Market/area name"
            value={detail.marketName}
            placeholder="Example: Greater Grand Forks"
            onChange={(value) => updateLocation(id, { marketName: value })}
          />
        </div>
      </QuestionFrame>
    );
  }

  if (step.id === "location-budgets") {
    return (
      <QuestionFrame
        eyebrow="Multi-location setup"
        title="Does each selected location have its own fixed marketing budget that must be spent specifically on that location?"
        headingRef={headingRef}
      >
        <div className="option-stack">
          <ChoiceCard
            label="No — HVAC Launch can distribute the total budget across locations based on opportunity and performance."
            description="Recommended for overall performance."
            badge="Recommended"
            selected={answers.locationBudgetMode === "flexible"}
            onClick={() => update({ locationBudgetMode: "flexible" })}
          />
          <ChoiceCard
            label="Yes — Each location has a separate budget that must be spent only on that location."
            selected={answers.locationBudgetMode === "fixed"}
            onClick={() => update({ locationBudgetMode: "fixed" })}
          />
        </div>
      </QuestionFrame>
    );
  }

  if (step.id === "location-schedules") {
    return (
      <QuestionFrame
        eyebrow="Multi-location setup"
        title="Do these locations share the same sales appointment schedule?"
        headingRef={headingRef}
      >
        <div className="option-stack">
          <ChoiceCard
            label="Separate schedules"
            description="Each location has its own sales reps, calendar, or availability."
            icon={<CalendarCheck />}
            selected={answers.locationScheduleMode === "separate"}
            onClick={() => update({ locationScheduleMode: "separate" })}
          />
          <ChoiceCard
            label="One shared schedule"
            description="Appointments for every location are booked using the same team and availability."
            icon={<UsersRound />}
            selected={answers.locationScheduleMode === "shared"}
            onClick={() => update({ locationScheduleMode: "shared" })}
          />
        </div>
      </QuestionFrame>
    );
  }

  if (step.id.startsWith(STEP_PREFIX.serviceArea)) {
    const id = step.id.slice(STEP_PREFIX.serviceArea.length);
    const location = client.locations.find((item) => item.id === id)!;
    const detail = answers.locationDetails[id];
    const zipCount = parseZipCodes(detail.serviceZipCodes).length;
    const hasMultipleLocations = selectedLocations(client, answers).length > 1;
    return (
      <QuestionFrame
        eyebrow={hasMultipleLocations ? `${location.name} • Service area & ad targeting` : "Service area & ad targeting"}
        title={hasMultipleLocations
          ? `Enter the ZIP codes you service for ${location.name}`
          : "Enter the ZIP codes you service"}
        description="We generally recommend advertising across your full service area for the best results."
        headingRef={headingRef}
      >
        <ZipField
          label="Service ZIP codes"
          value={detail.serviceZipCodes}
          onChange={(value) => updateLocation(id, { serviceZipCodes: value })}
        />
        <div className="zip-status">
          <MapPin size={18} />
          <strong>{zipCount || "No"} ZIP{zipCount === 1 ? "" : "s"} detected</strong>
          <span>We’ll validate the final audience before launch.</span>
        </div>
      </QuestionFrame>
    );
  }

  if (step.id === "sales-role") {
    return (
      <QuestionFrame
        eyebrow="Your team"
        title="What do you call your sales reps?"
        description="Every company is different. Examples: Comfort Advisors, Design Technicians, Comfort Specialist, etc."
        headingRef={headingRef}
      >
        <TextField
          label="Sales representative title"
          value={answers.salesRepTitle}
          placeholder="Comfort Advisor"
          autoFocus
          onChange={(value) => update({ salesRepTitle: value })}
        />
        <ExampleChips values={["Comfort Advisor", "Design Technician", "Comfort Specialist"]} onSelect={(salesRepTitle) => update({ salesRepTitle })} />
      </QuestionFrame>
    );
  }

  if (step.id === "quality-role") {
    return (
      <QuestionFrame
        eyebrow="Your team"
        title="What do you call your post-installation quality role?"
        description="This is the person who handles the final touch, inspection, or customer quality check. Typically 1-2 weeks post-install. Not all companies have this role."
        headingRef={headingRef}
      >
        <TextField
          label="Post-installation / quality-assurance title"
          value={answers.qaRoleTitle}
          placeholder="Quality Assurance Technician"
          disabled={answers.noQaRole}
          onChange={(value) => update({ qaRoleTitle: value, noQaRole: false })}
        />
        <CheckRow
          checked={answers.noQaRole}
          onChange={(checked) =>
            update({ noQaRole: checked, qaRoleTitle: checked ? "" : answers.qaRoleTitle })
          }
          label="We don’t have this role"
        />
      </QuestionFrame>
    );
  }

  if (step.id === "email-platform") {
    return (
      <QuestionFrame
        eyebrow="Your systems"
        title="Which email platform does your company use?"
        description="This way we can deliver lead notifications to your team’s email inbox properly."
        headingRef={headingRef}
      >
        <div className="choice-grid three email-platform-grid">
          <ChoiceCard
            label="Gmail"
            icon={<Mail />}
            selected={answers.emailPlatform === "gmail"}
            onClick={() => update({ emailPlatform: "gmail", otherEmailPlatform: "" })}
          />
          <ChoiceCard
            label="Microsoft Outlook"
            icon={<Laptop2 />}
            selected={answers.emailPlatform === "outlook"}
            onClick={() => update({ emailPlatform: "outlook", otherEmailPlatform: "" })}
          />
          <ChoiceCard
            label="Another platform"
            icon={<CircleHelp />}
            selected={answers.emailPlatform === "other"}
            onClick={() => update({ emailPlatform: "other" })}
          />
        </div>
        {answers.emailPlatform === "other" && (
          <TextField
            label="Email platform"
            value={answers.otherEmailPlatform}
            placeholder="Tell us which platform"
            onChange={(otherEmailPlatform) => update({ otherEmailPlatform })}
          />
        )}
      </QuestionFrame>
    );
  }

  if (step.id === "fsm") {
    return (
      <QuestionFrame
        eyebrow="Your systems"
        title="What is your field service management software?"
        description="We’ll use this to prepare tracking and access requirements for your kickoff call."
        headingRef={headingRef}
      >
        <div className="option-stack">
          <ChoiceCard
            label="ServiceTitan"
            selected={answers.fsm === "servicetitan"}
            onClick={() => update({ fsm: "servicetitan", otherFsm: "" })}
          />
          <ChoiceCard
            label="Housecall Pro"
            selected={answers.fsm === "housecall-pro"}
            onClick={() =>
              update({
                fsm: "housecall-pro",
                otherFsm: "",
                serviceTitanMarketingPro: "",
              })
            }
          />
          <ChoiceCard
            label="Other software"
            selected={answers.fsm === "other"}
            onClick={() =>
              update({ fsm: "other", serviceTitanMarketingPro: "" })
            }
          />
        </div>
        {answers.fsm === "other" && (
          <TextField
            label="Field service management software"
            value={answers.otherFsm}
            placeholder="Tell us which software"
            onChange={(otherFsm) => update({ otherFsm })}
          />
        )}
      </QuestionFrame>
    );
  }

  if (step.id === "marketing-pro") {
    return (
      <QuestionFrame
        eyebrow="ServiceTitan setup"
        title="Do you have the Marketing Pro subscription?"
        description="Marketing Pro is a separate paid add-on and is not typically included in a standard ServiceTitan subscription."
        headingRef={headingRef}
      >
        <div className="choice-grid three">
          {(["yes", "no", "unsure"] as const).map((value) => (
            <ChoiceCard
              key={value}
              label={value === "yes" ? "Yes" : value === "no" ? "No" : "I’m unsure"}
              selected={answers.serviceTitanMarketingPro === value}
              onClick={() => update({ serviceTitanMarketingPro: value })}
            />
          ))}
        </div>
        <InsightNote>
          Unsure is completely fine—we can verify it together during kickoff.
        </InsightNote>
      </QuestionFrame>
    );
  }

  if (step.id === "shared-inbox") {
    return (
      <QuestionFrame
        eyebrow="Lead response"
        title="When you get a form fill from your website, what email does it get sent to?"
        description="Most HVAC companies use a shared leads inbox."
        headingRef={headingRef}
      >
        <p className="inline-example">Example: leads@xyz.com; office@xyz.com; customerservice@xyz.com</p>
        <TextField
          label="Shared inbox"
          type="email"
          value={answers.sharedInbox}
          placeholder="leads@company.com"
          disabled={answers.needsSharedInbox}
          onChange={(sharedInbox) => update({ sharedInbox, needsSharedInbox: false })}
        />
        <CheckRow
          checked={answers.needsSharedInbox}
          onChange={(needsSharedInbox) =>
            update({
              needsSharedInbox,
              sharedInbox: needsSharedInbox ? "" : answers.sharedInbox,
            })
          }
          label="We need to create a dedicated shared inbox first"
        />
      </QuestionFrame>
    );
  }

  if (step.id === "lead-owner") {
    return (
      <QuestionFrame
        eyebrow="Lead response"
        title="Who will contact and book these leads?"
        description={
          <>
            For larger HVAC companies, this is an ISR (Internal Sales Rep).<br />
            For most companies, this is a CSR who already does a lot of outbound calling. (form fills, re-hash, etc)
          </>
        }
        headingRef={headingRef}
      >
        <PersonFields
          value={answers.primaryLeadContact}
          onChange={(primaryLeadContact) => update({ primaryLeadContact })}
        />
      </QuestionFrame>
    );
  }

  if (step.id === "office-manager") {
    return (
      <QuestionFrame
        eyebrow="Lead response"
        title="Do you have an office manager?"
        headingRef={headingRef}
      >
        <PersonFields
          value={answers.officeManager}
          disabled={answers.noOfficeManager}
          onChange={(officeManager) => update({ officeManager, noOfficeManager: false })}
        />
        <CheckRow
          checked={answers.noOfficeManager}
          onChange={(noOfficeManager) =>
            update({
              noOfficeManager,
              officeManager: noOfficeManager
                ? { name: "", email: "" }
                : answers.officeManager,
            })
          }
          label="We don’t have an office manager"
        />
      </QuestionFrame>
    );
  }

  if (step.id === "lead-notifications") {
    return (
      <QuestionFrame
        eyebrow="Lead response"
        title="Who else should receive every new lead notification?"
        description={
          <>
            Every time a lead comes in, who else should receive a notification?
            <span className="optional-badge">Optional</span>
          </>
        }
        headingRef={headingRef}
      >
        <EmailList
          values={answers.notificationEmails}
          onChange={(notificationEmails) => update({ notificationEmails })}
        />
      </QuestionFrame>
    );
  }

  if (step.id === "salespeople") {
    return (
      <QuestionFrame
        eyebrow="Lead response"
        title="Who will sell these leads?"
        description="It’s recommended that 1 dedicated salesperson runs these leads. Add the salespeople dedicated to these new leads:"
        headingRef={headingRef}
      >
        <PeopleList
          values={answers.salespeople}
          onChange={(salespeople) => update({ salespeople })}
        />
      </QuestionFrame>
    );
  }

  if (step.id === "sales-manager") {
    return (
      <QuestionFrame
        eyebrow="Lead response"
        title="Do you have a sales manager?"
        headingRef={headingRef}
      >
        <PersonFields
          value={answers.salesManager}
          disabled={answers.noSalesManager}
          onChange={(salesManager) => update({ salesManager, noSalesManager: false })}
        />
        <CheckRow
          checked={answers.noSalesManager}
          onChange={(noSalesManager) =>
            update({
              noSalesManager,
              salesManager: noSalesManager
                ? { name: "", email: "" }
                : answers.salesManager,
            })
          }
          label="We don’t have a sales manager"
        />
      </QuestionFrame>
    );
  }

  if (step.id === "media-sharing") {
    return (
      <QuestionFrame
        eyebrow="Creative library"
        title="How would you like to share photos and videos?"
        description={
          <>
            Most HVAC companies have photos and videos of their team, working on job-sites, and other fun stuff.
            <br /><br />
            You don’t need to upload anything now. Just tell us how you plan to share it, we’ll remind you during our kickoff call.
          </>
        }
        headingRef={headingRef}
      >
        <p className="brand-trust-copy">
          With your photos and videos, homeowners will trust and recognize {client.brandName}.
        </p>
        <div className="option-stack">
          <ChoiceCard
            label="Share an existing Drive, Dropbox, or OneDrive folder"
            description="Grant the HVAC Launch team access to the library you already use."
            badge="Recommended"
            icon={<Images />}
            selected={answers.mediaSharingMethod === "existing-folder"}
            onClick={() => update({ mediaSharingMethod: "existing-folder" })}
          />
          <ChoiceCard
            label="Upload to an HVAC Launch folder"
            description="We’ll provide a dedicated Google Drive location after submission."
            icon={<Plus />}
            selected={answers.mediaSharingMethod === "hvac-launch-folder"}
            onClick={() => update({ mediaSharingMethod: "hvac-launch-folder" })}
          />
          <ChoiceCard
            label="We need help finding usable media"
            description="We don’t currently have an accessible photo or video library."
            icon={<CircleHelp />}
            selected={answers.mediaSharingMethod === "need-help"}
            onClick={() => update({ mediaSharingMethod: "need-help" })}
          />
        </div>
      </QuestionFrame>
    );
  }

  if (step.id === "meeting-prep") {
    const fsmName =
      answers.fsm === "servicetitan"
        ? "ServiceTitan"
        : answers.fsm === "housecall-pro"
          ? "Housecall Pro"
          : answers.otherFsm || "your field service system";
    return (
      <QuestionFrame
        eyebrow="Kickoff call"
        title="Your kickoff call is coming up."
        description={`This will be a 1-hour strategy and setup session for your first Meta promotion. To keep your launch moving, please make sure the right person attends with access to Meta and ${fsmName}. If technical setup requires additional troubleshooting, we’ll complete it separately with your system administrator.`}
        headingRef={headingRef}
        tone="call-prep"
      >
        <div className="acknowledgement-list">
          <CheckRow
            checked={answers.meetingAcknowledgements.metaAdmin}
            onChange={(metaAdmin) =>
              update({
                meetingAcknowledgements: {
                  ...answers.meetingAcknowledgements,
                  metaAdmin,
                },
              })
            }
            label="Someone on our team will have administrator access to our Meta Business Portfolio"
          />
          <CheckRow
            checked={answers.meetingAcknowledgements.fsmAccess}
            onChange={(fsmAccess) =>
              update({
                meetingAcknowledgements: {
                  ...answers.meetingAcknowledgements,
                  fsmAccess,
                },
              })
            }
            label={`Someone on our team will have ${fsmName} access for tracking.`}
          />
          <CheckRow
            checked={answers.meetingAcknowledgements.delayRisk}
            onChange={(delayRisk) =>
              update({
                meetingAcknowledgements: {
                  ...answers.meetingAcknowledgements,
                  delayRisk,
                },
              })
            }
            label="We understand that missing access may delay launch."
          />
        </div>
      </QuestionFrame>
    );
  }

  if (step.id === "review") {
    const locations = selectedLocations(client, answers);
    return (
      <QuestionFrame
        eyebrow="Final review"
        title="Everything we need to prepare your launch."
        description="Take a quick look. Submitting will send these answers to your HVAC Launch team and open the kickoff calendar."
        headingRef={headingRef}
        wide
      >
        <div className="review-summary-grid">
        <SummaryCard
            icon={<Building2 />}
            title="Business & targeting"
            lines={[
              `${locations.length} selected ${locations.length === 1 ? "location" : "locations"}`,
              `${locations.reduce((total, location) => total + targetZipCodes(answers.locationDetails[location.id]).length, 0)} target ZIP codes`,
              locations.length > 1
                ? answers.locationScheduleMode === "shared"
                  ? "One shared sales schedule"
                  : "Separate sales schedules"
                : locations[0]?.name ?? "Single market",
              ...(locations.length > 1
                ? [answers.locationBudgetMode === "fixed"
                  ? "Separate fixed marketing budgets"
                  : "Flexible cross-location budget"]
                : []),
            ]}
            onEdit={() => onJump(
              client.locations.length === 1 && locations[0]
                ? `${STEP_PREFIX.reviewLocation}${locations[0].id}`
                : "locations",
            )}
          />
          <SummaryCard
            icon={<Laptop2 />}
            title="Team & systems"
            lines={[
              answers.salesRepTitle,
              answers.fsm === "servicetitan"
                ? "ServiceTitan"
                : answers.fsm === "housecall-pro"
                  ? "Housecall Pro"
                  : answers.otherFsm,
              answers.emailPlatform === "gmail"
                ? "Google Workspace"
                : answers.emailPlatform === "outlook"
                  ? "Microsoft Outlook"
                  : answers.otherEmailPlatform,
            ]}
            onEdit={() => onJump("sales-role")}
          />
          <SummaryCard
            icon={<UsersRound />}
            title="Lead response"
            lines={[
              answers.needsSharedInbox ? "Shared inbox needed" : answers.sharedInbox,
              [answers.primaryLeadContact.name, answers.primaryLeadContact.email]
                .filter(Boolean)
                .join(", "),
              `${answers.salespeople.length} dedicated ${answers.salespeople.length === 1 ? "salesperson" : "salespeople"}`,
            ]}
            onEdit={() => onJump("shared-inbox")}
          />
          <SummaryCard
            icon={<Images />}
            title="Creative & meeting"
            lines={[
              answers.mediaSharingMethod === "existing-folder"
                ? "Existing media folder"
                : answers.mediaSharingMethod === "hvac-launch-folder"
                  ? "HVAC Launch upload folder"
                  : "Media support needed",
              "Meeting access confirmed",
            ]}
            onEdit={() => onJump("media-sharing")}
          />
        </div>
        <div className="secure-submit-note">
          <ShieldCheck />
          <span>
            <strong>Your answers are private.</strong>
            They are used only to prepare and operate your HVAC Launch campaign.
          </span>
        </div>
      </QuestionFrame>
    );
  }

  if (step.id === "scheduler") {
    return (
      <SchedulerStep
        client={client}
        headingRef={headingRef}
        previewMode={previewMode}
        onMeetingBooked={onMeetingBooked}
      />
    );
  }

  return (
    <QuestionFrame
      eyebrow="Kickoff complete"
      title={
        <>
          {client.contactFirstName ? (
            <>
              You’re all set,{" "}
              <span className="red-text">{client.contactFirstName}.</span>
            </>
          ) : (
            "You’re all set."
          )}
        </>
      }
      headingRef={headingRef}
      wide
    >
      <div className="completion-mark"><Check /></div>
      <p className="lead-copy centered">
        Your answers are with the HVAC Launch team. We'll use them to prepare
        targeting, tracking, responsibilities, and recommendations before your
        kickoff call. Don't forget to accept the calendar invite that's sent to
        your email!
      </p>
      <div className="next-up">
        <strong>What happens next</strong>
        <ol>
          <li><span>1</span> We review your submission.</li>
          <li><span>2</span> We prepare launch and system recommendations.</li>
          <li><span>3</span> You make the final decisions together on your kickoff call.</li>
        </ol>
      </div>
      {booking && (
        <div className="booking-confirmation">
          <CalendarCheck />
          <div>
            <span>Kickoff call booked</span>
            <strong>
              {new Intl.DateTimeFormat("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                timeZoneName: "short",
              }).format(new Date(booking.startTime))}
            </strong>
          </div>
          {(booking.rescheduleUrl || booking.cancelUrl) && (
            <div className="booking-links">
              {booking.rescheduleUrl && (
                <a href={booking.rescheduleUrl} target="_blank" rel="noreferrer">
                  Reschedule
                </a>
              )}
              {booking.cancelUrl && (
                <a href={booking.cancelUrl} target="_blank" rel="noreferrer">
                  Cancel
                </a>
              )}
            </div>
          )}
        </div>
      )}
      <p className="completion-note">
        A confirmation will be sent to <strong>{client.clientEmail}</strong>.
        You can safely close this window.
      </p>
    </QuestionFrame>
  );
}

function QuestionFrame({
  eyebrow,
  title,
  description,
  headingRef,
  children,
  wide = false,
  tone,
}: {
  eyebrow: string;
  title: ReactNode;
  description?: ReactNode;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  children: ReactNode;
  wide?: boolean;
  tone?: "welcome" | "call-prep";
}) {
  return (
    <div className={`question-frame ${wide ? "is-wide" : ""} ${tone ? `is-${tone}` : ""}`}>
      <div className="eyebrow"><span />{eyebrow}</div>
      <h1 ref={headingRef} tabIndex={-1}>{title}</h1>
      {description && <p className="question-description">{description}</p>}
      <div className="question-body">{children}</div>
    </div>
  );
}

function ChoiceCard({
  label,
  description,
  badge,
  badgePlacement = "inline",
  icon,
  selected,
  onClick,
}: {
  label: string;
  description?: string;
  badge?: string;
  badgePlacement?: "inline" | "above";
  icon?: ReactNode;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`choice-card ${selected ? "is-selected" : ""}`}
      type="button"
      aria-pressed={selected}
      onClick={onClick}
    >
      {icon && <span className="choice-icon">{icon}</span>}
      <span className="choice-content">
        <span
          className={`choice-title ${badge ? "has-badge" : ""} ${badgePlacement === "above" ? "is-stacked" : ""}`}
        >
          {badge && badgePlacement === "above" && <em>{badge}</em>}
          <strong>{label}</strong>
          {badge && badgePlacement === "inline" && <em>{badge}</em>}
        </span>
        {description && <small>{description}</small>}
      </span>
      <span className="check-control">
        {selected && <Check size={17} strokeWidth={3} />}
      </span>
    </button>
  );
}

function TextField({
  label,
  value,
  placeholder,
  onChange,
  type = "text",
  disabled = false,
  autoFocus = false,
}: {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  type?: "text" | "email";
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <label className={`text-field ${disabled ? "is-disabled" : ""}`}>
      <span>{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete={type === "email" ? "email" : "off"}
      />
    </label>
  );
}

function ZipField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="text-field zip-field">
      <span>{label}</span>
      <textarea
        value={value}
        placeholder={"58201, 58202, 58203\n58102, 58103, 58104"}
        onChange={(event) => onChange(event.target.value)}
        inputMode="numeric"
        rows={5}
      />
    </label>
  );
}

function CheckRow({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <button
      className={`check-row ${checked ? "is-checked" : ""}`}
      type="button"
      aria-pressed={checked}
      onClick={() => onChange(!checked)}
    >
      <span className="check-control">
        {checked && <Check size={16} strokeWidth={3} />}
      </span>
      <span>{label}</span>
    </button>
  );
}

function EditableReviewCard({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  return (
    <div className={`review-card ${editing ? "is-editing" : ""}`}>
      <div className="review-value">
        <small>{label}</small>
        {editing ? (
          <input
            autoFocus
            value={value}
            placeholder={placeholder}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                setEditing(false);
              }
              if (event.key === "Escape") setEditing(false);
            }}
          />
        ) : (
          <strong>{value || <span className="missing-value">Needs an answer</span>}</strong>
        )}
      </div>
      <button
        className={editing ? "confirm-edit" : "edit-button"}
        type="button"
        aria-label={editing ? `Confirm ${label}` : `Edit ${label}`}
        onClick={() => setEditing(!editing)}
      >
        {editing ? <Check size={18} /> : <Edit3 size={18} />}
        <span>{editing ? "Confirm" : "Edit"}</span>
      </button>
    </div>
  );
}

function EditableCallCoverageCard({
  value,
  onChange,
}: {
  value: LocationAnswers;
  onChange: (patch: Partial<LocationAnswers>) => void;
}) {
  const [editing, setEditing] = useState(!value.callsCoverage);
  const summary = value.callsCoverage === "24-7"
    ? "Yes, 24/7"
    : value.callsCoverage === "custom"
      ? value.callHours || "No — hours needed"
      : "Needs an answer";
  return (
    <div className={`review-card review-card-expanded ${editing ? "is-editing" : ""}`}>
      <div className="review-card-heading">
        <span>
          <small>Call coverage</small>
          <strong>Are calls answered 24/7?</strong>
        </span>
        <button
          className={editing ? "confirm-edit" : "edit-button"}
          type="button"
          aria-label={editing ? "Confirm call coverage" : "Edit call coverage"}
          onClick={() => setEditing(!editing)}
        >
          {editing ? <Check size={18} /> : <Edit3 size={18} />}
          <span>{editing ? "Confirm" : "Edit"}</span>
        </button>
      </div>
      {editing ? (
        <>
          <div className="compact-options">
            <ChoiceCard
              label="Yes, 24/7"
              selected={value.callsCoverage === "24-7"}
              onClick={() => onChange({ callsCoverage: "24-7", callHours: "" })}
            />
            <ChoiceCard
              label="No"
              selected={value.callsCoverage === "custom"}
              onClick={() => onChange({ callsCoverage: "custom" })}
            />
          </div>
          {value.callsCoverage === "custom" && (
            <TextField
              label="Hours calls are answered"
              value={value.callHours}
              placeholder="Example: Mon–Fri 7am–7pm, Sat 8am–4pm"
              onChange={(callHours) => onChange({ callHours })}
            />
          )}
        </>
      ) : (
        <div className="review-confirmed-value">
          <CheckCircle2 size={18} />
          <strong className={!value.callsCoverage ? "missing-value" : ""}>{summary}</strong>
        </div>
      )}
    </div>
  );
}

function PersonFields({
  value,
  onChange,
  disabled = false,
  labelPrefix,
}: {
  value: { name: string; email: string };
  onChange: (value: { name: string; email: string }) => void;
  disabled?: boolean;
  labelPrefix?: string;
}) {
  return (
    <div className="person-fields">
      <TextField
        label={labelPrefix ? `${labelPrefix} name` : "Full name"}
        value={value.name}
        placeholder="Jordan Smith"
        disabled={disabled}
        onChange={(name) => onChange({ ...value, name })}
      />
      <TextField
        label={labelPrefix ? `${labelPrefix} work email` : "Work email"}
        type="email"
        value={value.email}
        placeholder="jordan@company.com"
        disabled={disabled}
        onChange={(email) => onChange({ ...value, email })}
      />
    </div>
  );
}

function PeopleList({
  values,
  onChange,
}: {
  values: Array<{ name: string; email: string }>;
  onChange: (values: Array<{ name: string; email: string }>) => void;
}) {
  const displayValues = values.length > 0 ? values : [{ name: "", email: "" }];
  const updateAtIndex = (index: number, value: { name: string; email: string }) => {
    const next = [...displayValues];
    next[index] = value;
    onChange(next);
  };

  return (
    <div className="people-list">
      {displayValues.map((person, index) => (
        <div className="person-row" key={index}>
          <PersonFields
            value={person}
            labelPrefix={`Salesperson ${index + 1}`}
            onChange={(value) => updateAtIndex(index, value)}
          />
          {(displayValues.length > 1 || person.name || person.email) && (
            <button
              className="icon-button"
              type="button"
              aria-label={`Remove salesperson ${index + 1}`}
              onClick={() => onChange(displayValues.filter((_, itemIndex) => itemIndex !== index))}
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      ))}
      <button
        className="add-button"
        type="button"
        onClick={() => onChange([...displayValues, { name: "", email: "" }])}
      >
        <Plus size={17} /> Add another salesperson
      </button>
    </div>
  );
}

function EmailList({
  values,
  onChange,
}: {
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const displayValues = values.length > 0 ? values : [""];
  const setAtIndex = (index: number, value: string) => {
    const next = [...displayValues];
    next[index] = value;
    onChange(next.filter((item, itemIndex) => item.trim() || itemIndex <= index));
  };
  return (
    <div className="email-list">
      {displayValues.map((email, index) => (
        <div className="email-row" key={index}>
          <TextField
            label={`Notification email ${index + 1}`}
            type="email"
            value={email}
            placeholder="teammate@company.com"
            onChange={(value) => setAtIndex(index, value)}
          />
          {(displayValues.length > 1 || email) && (
            <button
              className="icon-button"
              type="button"
              aria-label={`Remove notification email ${index + 1}`}
              onClick={() => onChange(displayValues.filter((_, itemIndex) => itemIndex !== index))}
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      ))}
      <button
        className="add-button"
        type="button"
        onClick={() => onChange([...displayValues, ""])}
      >
        <Plus size={17} /> Add another recipient
      </button>
    </div>
  );
}

function ExampleChips({
  values,
  onSelect,
}: {
  values: string[];
  onSelect: (value: string) => void;
}) {
  return (
    <div className="example-chips">
      {values.map((value) => (
        <button key={value} type="button" onClick={() => onSelect(value)}>
          {value}
        </button>
      ))}
    </div>
  );
}

function PromiseCard({
  icon,
  label,
  copy,
}: {
  icon: ReactNode;
  label: string;
  copy: string;
}) {
  return (
    <div className="promise-card">
      <span>{icon}</span>
      <strong>{label}</strong>
      <p>{copy}</p>
    </div>
  );
}

function InsightNote({ children }: { children: ReactNode }) {
  return (
    <div className="insight-note">
      <Sparkles size={18} />
      <span>{children}</span>
    </div>
  );
}

function SummaryCard({
  icon,
  title,
  lines,
  onEdit,
}: {
  icon: ReactNode;
  title: string;
  lines: string[];
  onEdit: () => void;
}) {
  return (
    <div className="summary-card">
      <div className="summary-heading">
        <span>{icon}</span>
        <strong>{title}</strong>
        <button type="button" onClick={onEdit}><Edit3 size={15} /> Edit</button>
      </div>
      <ul>
        {lines.filter(Boolean).map((line) => (
          <li key={line}><CheckCircle2 size={15} />{line}</li>
        ))}
      </ul>
    </div>
  );
}

function SchedulerStep({
  client,
  headingRef,
  previewMode,
  onMeetingBooked,
}: {
  client: ClientContext;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  previewMode: boolean;
  onMeetingBooked: (booking: BookingConfirmation) => void;
}) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [availability, setAvailability] =
    useState<SchedulingAvailability | null>(null);
  const [availabilityError, setAvailabilityError] = useState("");
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(true);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedTime, setSelectedTime] = useState("");

  const loadAvailability = () => {
    setIsLoadingAvailability(true);
    setAvailabilityError("");
    setSelectedDay("");
    setSelectedTime("");
    void loadSchedulingAvailability(timezone)
      .then((result) => setAvailability(result))
      .catch((error: unknown) =>
        setAvailabilityError(
          error instanceof Error
            ? error.message
            : "Available meeting times could not be loaded.",
        ),
      )
      .finally(() => setIsLoadingAvailability(false));
  };

  useEffect(() => {
    loadAvailability();
  }, []);

  const dayGroups = useMemo(() => {
    const groups = new Map<string, { day: Date; slots: string[] }>();
    for (const slot of availability?.slots ?? []) {
      const date = new Date(slot.startTime);
      const key = [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
      ].join("-");
      const group = groups.get(key) ?? { day: date, slots: [] };
      group.slots.push(slot.startTime);
      groups.set(key, group);
    }
    return Array.from(groups.entries()).slice(0, 4);
  }, [availability]);

  const selectedSlots =
    dayGroups.find(([key]) => key === selectedDay)?.[1].slots ?? [];

  const confirmTime = async () => {
    if (!selectedTime || isBooking) return;
    setIsBooking(true);
    setBookingError("");
    try {
      const confirmed = await bookOnboardingMeeting(selectedTime, timezone);
      onMeetingBooked(confirmed);
    } catch (error) {
      setBookingError(
        error instanceof Error
          ? error.message
          : "This meeting could not be booked.",
      );
      loadAvailability();
    } finally {
      setIsBooking(false);
    }
  };

  return (
    <QuestionFrame
      eyebrow={previewMode ? "Scheduler preview" : "Final step"}
      title="Book your kickoff call."
      description={`${client.brandName} HVAC Launch: Promo Strategy & Setup Call • 1 hour`}
      headingRef={headingRef}
      wide
    >
      {(previewMode || availability?.preview) && (
        <div className="preview-notice">
          <Sparkles size={17} />
          This preview uses simulated availability. The live form will use Calendly
          without exposing Calendly’s interface.
        </div>
      )}
      <div className="mock-scheduler">
        <div className="mock-calendar">
          <span className="scheduler-label">Choose a day</span>
          {isLoadingAvailability ? (
            <div className="scheduler-loading">
              <LoaderCircle className="spin" />
              Checking the team’s calendar…
            </div>
          ) : availabilityError ? (
            <div className="scheduler-error" role="alert">
              <AlertCircle />
              <span>{availabilityError}</span>
              <button type="button" onClick={loadAvailability}>Try again</button>
            </div>
          ) : dayGroups.length === 0 ? (
            <div className="scheduler-empty">
              No open times were found in the next two weeks. Please contact your
              client partner for help.
            </div>
          ) : (
            <div className="day-grid">
            {dayGroups.map(([key, group]) => (
              <button
                type="button"
                key={key}
                className={selectedDay === key ? "is-selected" : ""}
                onClick={() => {
                  setSelectedDay(key);
                  setSelectedTime("");
                  setBookingError("");
                }}
              >
                <small>{new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(group.day)}</small>
                <strong>{group.day.getDate()}</strong>
                <span>{new Intl.DateTimeFormat("en-US", { month: "short" }).format(group.day)}</span>
              </button>
            ))}
            </div>
          )}
        </div>
        <div className="mock-times">
          <span className="scheduler-label">
            {!selectedDay ? "Select a day to see times" : "Choose a time"}
          </span>
          <div className="time-grid">
            {selectedSlots.map((startTime) => (
              <button
                key={startTime}
                type="button"
                className={selectedTime === startTime ? "is-selected" : ""}
                onClick={() => {
                  setSelectedTime(startTime);
                  setBookingError("");
                }}
              >
                {new Intl.DateTimeFormat("en-US", {
                  hour: "numeric",
                  minute: "2-digit",
                }).format(new Date(startTime))}
              </button>
            ))}
          </div>
        </div>
      </div>
      {bookingError && (
        <div className="scheduler-booking-error" role="alert">
          <AlertCircle />
          {bookingError}
        </div>
      )}
      <div className="scheduler-actions">
        <span className="timezone-note">
          <Clock3 size={15} />
          Times shown in {timezone.replaceAll("_", " ")}
        </span>
        <button
          className="button button-primary"
          type="button"
          disabled={!selectedDay || !selectedTime || isBooking}
          onClick={() => void confirmTime()}
        >
          {isBooking ? (
            <>
              <LoaderCircle className="spin" size={18} />
              Booking your call
            </>
          ) : (
            <>
              Confirm this time <CalendarCheck size={18} />
            </>
          )}
        </button>
      </div>
    </QuestionFrame>
  );
}

function initialsForName(name: string | undefined): string {
  if (!name) return "HL";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function SaveIndicator({
  state,
  mode,
}: {
  state: SaveState;
  mode: "preview" | "live";
}) {
  if (state === "saving") {
    return <span className="save-state"><LoaderCircle className="spin" />Saving…</span>;
  }
  if (state === "error") {
    return <span className="save-state is-error"><AlertCircle />Save interrupted</span>;
  }
  if (state === "conflict") {
    return (
      <span className="save-state is-error">
        <AlertCircle />Newer copy detected—reload to reconcile
      </span>
    );
  }
  return (
    <span className="save-state">
      <CheckCircle2 />
      {mode === "preview" ? "Saved in this browser" : "Answers saved"}
    </span>
  );
}

function Atmosphere() {
  return (
    <div className="atmosphere" aria-hidden="true">
      <div className="red-glow" />
      <div className="blue-glow" />
      <div className="grid-texture" />
      <div className="scan-line" />
    </div>
  );
}

function FullPageState({
  icon,
  eyebrow,
  title,
  copy,
  action,
  variant = "error",
}: {
  icon: ReactNode;
  eyebrow: string;
  title: string;
  copy: string;
  action?: ReactNode;
  variant?: "error" | "success";
}) {
  return (
    <main className="full-page-state">
      <Atmosphere />
      <img src={LOGO_URL} className="brand-logo" alt="HVAC Launch" />
      <div
        className={`state-card is-${variant}`}
        role={variant === "success" ? "status" : undefined}
      >
        <span className="state-icon">{icon}</span>
        <div className="eyebrow"><span />{eyebrow}</div>
        <h1>{title}</h1>
        <p>{copy}</p>
        {action}
      </div>
    </main>
  );
}

export default App;
